import { useEffect, useRef, useState, type ReactNode } from 'react';
import { PageShell } from '@resource-kit/ui/react';
import { useToday } from '@resource-kit/controllers/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@components/ui/Toast';
import { http } from '@lib/api';
import { queryKeys } from '@lib/queryKeys';
import type { components } from '@/types/api.generated';
import { wallTimeToUtcIso } from '@resource-kit/controllers';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useBetterAuth, usePermissions } from '@hooks/useAuth';
import { PERMISSIONS } from '@/permissions';
import { resolveApiError } from '@lib/errors';
import { usePendingReviewCount } from '@hooks/usePendingReviewCount';
import { useMinuteNow } from '@hooks/useMinuteNow';
import { formatBatchDay } from '../board/formatBatchDay';
import {
  runDispatchChunked,
  type DispatchRunChunkResult,
  type DispatchRunAccumulated,
} from '../runDispatchChunked';

// A dispatch chunk performs sequential Onfleet reads/writes. Give the backend
// enough time to surface its own bounded provider timeout instead of letting
// the shared Axios default (API_DEFAULT_TIMEOUT_MS in @lib/api) mask the
// result as a client cancellation.
const DISPATCH_RUN_REQUEST_TIMEOUT_MS = 60_000;
import { RoutePlanningSection } from '../components/RoutePlanningSection';
import { DispatchJobList } from '../list/DispatchJobList';
import { DispatchDetailPane } from '../components/DispatchDetailPane';
import {
  DispatchFlowChip,
  DispatchHeaderActions,
  type DispatchView,
} from '../components/DispatchPageHeader';
import { AssignmentPane } from '../components/AssignmentPane';
import { DispatchConfirmDialog } from '../components/DispatchConfirmDialog';
import { PendingPane } from '../panes/PendingPane';
import { DeliveredPane } from '../panes/DeliveredPane';
import { EnRoutePane } from '../panes/EnRoutePane';
import { DeparturePane } from '../panes/DeparturePane';
import { ExceptionPane } from '../panes/ExceptionPane';
import { BatchBoard } from '../board/BatchBoard';
import { FocusCards } from '../board/FocusCardGrid';
import {
  BOARD_STAGES,
  DEFAULT_BOARD_RANGE_DAYS,
  boardWindow,
  buildBoardLines,
  dayTone,
  parseBoardStage,
  type BoardStage,
} from '../board/boardModel';
import { useBoardDays } from '../board/useBoardDays';
import { toBoardStages } from '../board/boardWire';
import { summarizeRouteEta } from '../board/etaSignals';
import { flowGateTime } from '../board/flowCopy';
import { useDeliveryFlow } from '../hooks/useDeliveryFlow';
import { useDispatchPreview } from '../hooks/useDispatchPreview';
import { useFocusCards } from '../board/useFocusCards';
import { useRouteEta } from '../hooks/useRouteEta';
import { useIsPhone } from '@hooks/useMediaQuery';
import { PhoneDispatch } from '../phone/PhoneDispatch';
import { shownSoldOut } from '../pending/soldOut';
import { addCalendarDays } from '@lib/calendar-day';

function parseWorkDate(raw: string | null): string | null {
  if (!raw || !/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
  // A day that overflows (`2026-02-31`) normalizes to another day.
  return addCalendarDays(raw, 0) === raw ? raw : null;
}

// `?tab=` value → view. `route-planning` is also an external entry
// (`App.tsx` redirects to it), so it keeps its name. The board has no tab.
const TAB_VIEW = {
  'route-planning': 'map',
  list: 'list',
} as const satisfies Record<string, Exclude<DispatchView, 'board'>>;

const VIEW_TAB = {
  map: 'route-planning',
  list: 'list',
} as const satisfies Record<Exclude<DispatchView, 'board'>, keyof typeof TAB_VIEW>;

function parseTabView(raw: string | null): Exclude<DispatchView, 'board'> | null {
  // Own keys only: `?tab=constructor` must not reach the prototype.
  return raw !== null && Object.prototype.hasOwnProperty.call(TAB_VIEW, raw)
    ? TAB_VIEW[raw as keyof typeof TAB_VIEW]
    : null;
}

// The route ETA query moved to `hooks/useRouteEta.ts` (#2487).
type ReconcileReport = components['schemas']['ReconcileReport'];
type TruthReconcileReport = components['schemas']['DispatchTruthReconcileReport'];
type SalesBridge = components['schemas']['SalesBridge'];

/**
 * `/dispatch` (#2487): the phone view below md, the desktop board otherwise.
 * Chosen in JS so the desktop page, its requests and its hooks never mount on
 * a phone.
 */
export default function DispatchDashboard() {
  return useIsPhone() ? <PhoneDispatch /> : <DispatchBoardView />;
}

function DispatchBoardView() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation();

  const perms = usePermissions();
  const canViewDispatch = perms.includes(PERMISSIONS.DISPATCH_READ);
  const { isAdmin } = useBetterAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  // #2438: the board is anchored on today and only moves when the selected
  // date falls outside it (`boardWindow`, epic #2469 C.3). Nothing below reads
  // it, so a failed or pending board GET never takes the dispatch controls
  // down with it.
  const boardToday = useToday();
  const date = parseWorkDate(searchParams.get('date')) ?? boardToday;
  const setWorkDate = (next: string) =>
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set('date', next);
        return params;
      },
      { replace: true },
    );
  // #2438: the selected batch-board column. A cell is a (day, column) pair, so
  // selecting one sets the shared work date too — every section below follows
  // `date` — and the column goes to the `_`-prefixed `_stage`.
  const stage = parseBoardStage(searchParams.get('_stage'));
  const selectCell = (nextDate: string, nextStage: BoardStage) =>
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set('date', nextDate);
        params.set('_stage', nextStage);
        return params;
      },
      { replace: true },
    );
  // The page shows the batch board, the job list (`?tab=list`, #2516) or the
  // route map (`?tab=route-planning`, epic #2469 C.4); all share the work date,
  // and the list and the map sit behind `dispatch:read`. A view switch PUSHES
  // a history entry so Back returns to the previous view; the date and cell
  // pickers above replace. Re-selecting the current view replaces too: the
  // router does not dedupe, and a duplicate entry makes one Back look dead.
  const tabView = parseTabView(searchParams.get('tab'));
  const view: DispatchView = tabView !== null && canViewDispatch ? tabView : 'board';
  const selectView = (next: DispatchView) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === 'board') params.delete('tab');
        else params.set('tab', VIEW_TAB[next]);
        return params;
      },
      { replace: next === view },
    );
  };

  // #1553: consolidate same-address same-day orders into one Onfleet task per
  // Stop. Default off = one task per job (the zero-risk, byte-identical path);
  // when on, the run body carries `consolidate: true` (additive — omitted when
  // off so the request stays identical to today's).
  const [consolidate, setConsolidate] = useState(false);

  // #2475: the standing consolidated dry-run for the work date. No per-item
  // recipient/address rows and no shadow-mode branch — those surfaces have no
  // backing data on the rebuilt backend.
  const preview = useDispatchPreview(date);
  const refreshPreview = () => preview.refetch();

  // The shared inbox count: open AND raised today, the same question the
  // operator inbox's Pending tab asks. This page used to run its own unbounded
  // `status=open` query, so the review chip — which navigates to that very tab —
  // reported every open message ever filed while the tab showed today's. A
  // message too old to act on is not something to put in front of an operator
  // planning the day. (#935 PR-D: the rebuilt review service has no `/count`
  // route, so the number is still the list `total`.)
  const reviewQueue = usePendingReviewCount();

  // #2473: the board's next-gate copy counts down in minutes, so the page
  // re-renders each minute on its own; no polling interval, no ETA polling.
  const nowMs = useMinuteNow();
  const flow = useDeliveryFlow();
  // A failed flow GET falls back to the phase-1 defaults rather than blanking
  // the board; the board GET waits only for a flow that is still loading.
  // The focus cards get no such fallback: they report the failed read.
  const rangeDays = flow.data?.board_range_days ?? DEFAULT_BOARD_RANGE_DAYS;
  const boardStages = flow.data ? toBoardStages(flow.data.columns) : BOARD_STAGES;
  const boardWin = boardWindow(boardToday, date, rangeDays);
  const board = useBoardDays(boardWin, !flow.isLoading);
  // #2473: the server owns `next_gate`, so once a row's gate instant has
  // passed the board is read again to get the next one. Once per instant:
  // the new gate is a later instant, and a stale one never fires twice.
  const passedGateMs = board.data?.reduce<number | undefined>((earliest, day) => {
    const at = day.nextGate ? Date.parse(day.nextGate.atIso) : Number.NaN;
    return at <= nowMs && (earliest === undefined || at < earliest) ? at : earliest;
  }, undefined);
  const refetchedGateMs = useRef<number | undefined>(undefined);
  const refetchBoard = board.refetch;
  useEffect(() => {
    if (passedGateMs === undefined || passedGateMs === refetchedGateMs.current) return;
    refetchedGateMs.current = passedGateMs;
    void refetchBoard();
  }, [passedGateMs, refetchBoard]);

  const salesBridge = useQuery({
    queryKey: queryKeys.dispatch.salesBridge(date),
    queryFn: () =>
      http.get<SalesBridge>('/api/dispatch/sales-bridge', {
        params: { date },
      }),
    staleTime: 60_000,
  });
  const soldOutShown = shownSoldOut(salesBridge.data);

  // #1128: route ETAs auto-refresh every 60s (matching the backend KV cache),
  // but only while the tab is focused — an idle dashboard shouldn't hammer the
  // Onfleet pull. `http.get` unwraps the envelope to the report directly.
  // #2473: the board view reads the same query (same key) when today has jobs
  // en route, for the late-driver copy on today's row. Read-only.
  const todayEnRoute = board.data?.find((day) => day.date === boardToday)?.counts.en_route ?? 0;
  // #2483: the en-route pane reads it too, even before the board has loaded.
  const shouldFetchEta =
    canViewDispatch &&
    (view === 'map' ||
      todayEnRoute > 0 ||
      (view === 'board' && stage === 'en_route' && date === boardToday));

  const eta = useRouteEta(shouldFetchEta);

  const windowEnd = flow.data ? flowGateTime(flow.data, 'delivery_window_end') : null;
  const etaSummary =
    eta.data && flow.data && windowEnd !== null
      ? summarizeRouteEta(
          eta.data,
          Date.parse(wallTimeToUtcIso(`${boardToday}T${windowEnd}:00.000`, flow.data.timezone)),
        )
      : undefined;

  // #2485: today's cards. Called here, not where they mount: a hook cannot sit
  // behind the mount condition. Off wherever the cards are not shown (the map
  // view, or a window moved off today), so nothing polls for a hidden card.
  const showFocus = view === 'board' && boardWin.anchor === 'today';
  const focusCards = useFocusCards({
    today: boardToday,
    days: board.data ?? [],
    flow,
    eta: etaSummary,
    nowMs,
    enabled: showFocus,
  });

  const invalidateDispatchScopes = () => {
    // A run creates fulfillment jobs + executions, so all three dispatch
    // scopes go stale — as queryKeys.ts documents, the invalidation must
    // list dispatch, dispatchJobs AND dispatchExecutions explicitly
    // (positional-tuple prefix matching means ['dispatch'] is not a
    // prefix of ['dispatch/jobs', ...]). The fulfillment-job list under
    // the `tasks` root reflects the same new jobs, so refresh it too
    // (#1155).
    queryClient.invalidateQueries({ queryKey: queryKeys.dispatch.all() });
    queryClient.invalidateQueries({ queryKey: queryKeys.dispatchJobs.all() });
    queryClient.invalidateQueries({ queryKey: queryKeys.dispatchExecutions.all() });
    queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all() });
  };

  // The dispatch header exposes ONE "Reconcile" action that checks BOTH truth
  // sources for the selected date at once (#1584-followup): the Onfleet side
  // (this `reconcile` mutation → `POST /api/reconcile-report`, rendered by
  // `ReconcileCheckCard`) and the Shopify side (the `truthReconcile` query
  // below → `GET /api/dispatch/truth-reconcile`, rendered by
  // `TruthReconcileCard`). Lifting the truth query here (previously owned by
  // `TruthReconcileCard`) lets the single action fan out to both and drive one
  // combined loading state. The query key is unchanged so `SalesBridgeCard`'s
  // passive read of the same cache slot keeps working.
  const reconcile = useMutation<ReconcileReport, unknown, string>({
    mutationFn: (deliveryDate) =>
      http.post<ReconcileReport>('/api/reconcile-report', { date: deliveryDate }),
  });
  const [truthTriggered, setTruthTriggered] = useState(false);
  const truthReconcile = useQuery({
    queryKey: queryKeys.dispatch.truthReconcile(date),
    queryFn: () =>
      http.get<TruthReconcileReport>('/api/dispatch/truth-reconcile', {
        params: { date },
      }),
    // Gated behind the unified action — an on-demand check, not a passive read.
    // The query key carries `date`, so a date change while triggered refetches;
    // a repeat click on an unchanged date is handled by an explicit `refetch()`.
    enabled: truthTriggered,
    staleTime: 30_000,
  });

  // The single reconcile action: fire the Onfleet-side reconcile and the
  // Shopify-side truth check together. First click arms the truth query; a
  // repeat click (same date, so the query would not auto-refetch) forces it.
  const handleReconcileAll = () => {
    reconcile.mutate(date);
    if (truthTriggered) void truthReconcile.refetch();
    else setTruthTriggered(true);
  };
  const reconcilePending = reconcile.isPending || truthReconcile.isFetching;

  // #935: the run is `POST /api/dispatch/run`, chunked + re-entrant. It takes a
  // JSON body `{ date }` and returns the UNWRAPPED `DispatchRunResult`
  // `{ date, dispatched, skipped, remaining }`; the client loops until
  // `remaining === 0` via the pure, unit-tested `runDispatchChunked` helper.
  // Re-running is idempotent, so a retry never double-dispatches. There is no
  // dry-run mode — the preview card above IS the dry-run.
  const [runProgress, setRunProgress] = useState<{
    dispatched: number;
    skipped: number;
    remaining: number;
    chunks: number;
  } | null>(null);
  const dispatchMutation = useMutation<DispatchRunAccumulated, unknown, string>({
    mutationFn: (runDate) =>
      runDispatchChunked(
        (body) =>
          http.post<DispatchRunChunkResult>(
            '/api/dispatch/run',
            consolidate ? { ...body, consolidate: true } : body,
            { timeout: DISPATCH_RUN_REQUEST_TIMEOUT_MS },
          ),
        runDate,
        (progress) => setRunProgress(progress),
      ),
    onSuccess: (result) => {
      setRunProgress(null);
      invalidateDispatchScopes();
      queryClient.invalidateQueries({ queryKey: queryKeys.reviewQueue.all() });
      void refreshPreview();
      // `remaining > 0` means the loop stopped early on a no-progress chunk
      // (e.g. Onfleet create failures): surface it as a partial result so the
      // operator knows the day did not fully drain.
      if (result.remaining > 0) {
        toast.error(
          t('dispatch.dispatchPartial'),
          t('dispatch.runPartialSummary', {
            date: result.date,
            dispatched: result.dispatched,
            remaining: result.remaining,
          }),
        );
        return;
      }
      toast.info(
        t('dispatch.dispatchCompleted'),
        t('dispatch.runSummary', {
          date: result.date,
          dispatched: result.dispatched,
        }),
      );
    },
    onError: (err) => {
      setRunProgress(null);
      console.error('[dispatch] run failed:', err);
      toast.error(
        t('dispatch.dispatchFailed'),
        resolveApiError(err, i18n, { namespace: 'dispatch', fallbackKey: 'error.unknown' }),
      );
    },
  });
  const previewData = preview.data ?? null;
  // #2475: delivery jobs a run sends. A missing field yields 0 (run
  // disabled), never NaN — `NaN === 0` is false and would arm the button.
  const pendReady = Math.max(
    0,
    (previewData?.plannedTotal ?? 0) - (previewData?.anomalies.length ?? 0),
  );
  const tasks = consolidate ? (previewData?.dispatchable ?? 0) : pendReady;
  const mergeableStops = previewData?.mergeableStops ?? 0;
  const packagingCutoff = flow.data?.gates.find((g) => g.kind === 'packaging_cutoff');
  const dispatchCutoff = flow.data?.gates.find((g) => g.kind === 'dispatch_cutoff');
  const gates =
    packagingCutoff && dispatchCutoff
      ? { packaging: packagingCutoff.local_time, dispatch: dispatchCutoff.local_time }
      : null;
  // #1533: KPI-1 now headlines the whole-day job `total` with a per-status
  // breakdown, replacing the misleading `plannedTotal`-only count.
  const total = previewData?.total ?? 0;
  const statusCounts = previewData?.statusCounts;
  const totalReviewItems = reviewQueue.data?.total ?? 0;
  const showReconcile =
    reconcile.variables === date &&
    (reconcile.isPending || reconcile.data !== undefined || reconcile.isError);

  // #2475: the run card's own confirmation. It captures the work date when it
  // opens and the run posts that date; it is shown only while the URL work
  // date still equals it, so it never describes one day and posts another.
  // Opening it refreshes the dry-run (the confirm waits for the refetch).
  const [confirmDate, setConfirmDate] = useState<string | null>(null);
  useEffect(() => setConfirmDate(null), [date]);
  const handleRunDispatch = () => {
    setConfirmDate(date);
    void refreshPreview();
  };
  const handleConfirmRun = () => {
    if (confirmDate === null) return;
    dispatchMutation.mutate(confirmDate);
    setConfirmDate(null);
  };

  // The selected cell's board row, for counts only: every pane renders from
  // the URL alone, so a pending or failed board GET hides a count, never a pane.
  const selectedDay = board.data?.find((day) => day.date === date);

  // #2439: one detail pane per board column. Queries and writes stay on this
  // page, so the reconcile result survives switching cells.
  //
  // WIRING CONVENTION (epic #2469): each value below is ONE element. A later
  // slice adds `panes/<X>Pane.tsx` and replaces only its own column's value
  // here — no other column's line moves.
  const paneBody: Record<BoardStage, ReactNode> = {
    awaiting_dispatch: (
      <PendingPane
        date={date}
        runCard={{
          today: boardToday,
          gates,
          pendReady,
          tasks,
          mergeableStops,
          consolidate,
          onConsolidateChange: setConsolidate,
          disabled: preview.isLoading,
          running: dispatchMutation.isPending,
          runProgress,
          onRun: handleRunDispatch,
        }}
        totalReviewItems={totalReviewItems}
        total={total}
        statusCounts={statusCounts}
        anomalies={previewData?.anomalies}
        previewError={preview.isError}
        salesBridge={salesBridge.data}
        salesBridgeError={salesBridge.isError}
        stopAlreadyDispatched={consolidate ? (previewData?.stopAlreadyDispatched ?? []) : []}
        previewLoading={preview.isLoading}
        reviewLoading={reviewQueue.isLoading}
      />
    ),
    awaiting_assignment: (
      <AssignmentPane date={date} onGoReady={() => selectCell(date, 'awaiting_departure')} />
    ),
    awaiting_departure: <DeparturePane date={date} />,
    en_route: (
      <EnRoutePane date={date} isToday={date === boardToday} flow={flow.data} eta={eta.data} />
    ),
    delivered: (
      <DeliveredPane
        date={date}
        onReconcile={handleReconcileAll}
        reconcilePending={reconcilePending}
        truth={{
          triggered: truthTriggered,
          report: truthReconcile.data,
          isFetching: truthReconcile.isFetching,
          isError: truthReconcile.isError,
          error: truthReconcile.error,
        }}
        reconcile={{
          triggered: showReconcile,
          report: reconcile.data,
          error: reconcile.isError ? reconcile.error : undefined,
          isPending: reconcile.isPending,
        }}
      />
    ),
    exception_or_cancelled: <ExceptionPane date={date} />,
  };

  return (
    <PageShell
      title={t('dispatch.title')}
      titleClassName="text-xl"
      titleAdornment={
        flow.data ? <DispatchFlowChip flow={flow.data} isAdmin={isAdmin} /> : undefined
      }
      actions={
        <DispatchHeaderActions
          view={view}
          onSelectView={selectView}
          date={date}
          onPickDate={setWorkDate}
          boardWin={boardWin}
          canViewDispatch={canViewDispatch}
        />
      }
    >
      {view === 'map' ? (
        <RoutePlanningSection date={date} eta={eta} />
      ) : view === 'list' ? (
        // Keyed by date: the list keeps its page and search in local state, and
        // page 2 of a busy day would read as "no results" on a quieter one.
        <DispatchJobList key={date} date={date} />
      ) : (
        <>
          {/* Board data or nothing: an empty card list would read as "all
              clear" while the board is still loading or failed. The cards
              describe today only, so they go when the window moves
              off it (#2485 keeps it that way). */}
          {board.isSuccess && showFocus ? (
            <FocusCards
              cards={focusCards.cards}
              allClear={focusCards.allClear}
              failed={focusCards.failed}
              onSelect={(target) => selectCell(target.date, target.stage)}
            />
          ) : null}
          <BatchBoard
            lines={buildBoardLines(board.data ?? [], boardWin, boardToday, date)}
            stages={boardStages}
            selected={{ date, stage }}
            onSelect={selectCell}
            loading={flow.isLoading || board.isLoading}
            isError={board.isError}
            flow={flow.data}
            now={new Date(nowMs)}
            eta={etaSummary}
            window={boardWin}
            soldOut={soldOutShown ? { date, count: soldOutShown.length } : undefined}
          />
          <DispatchDetailPane
            date={date}
            stage={stage}
            count={selectedDay?.counts[stage]}
            rowTotal={selectedDay?.total}
            tone={dayTone(date, boardToday)}
            boardReady={board.isSuccess}
          >
            {paneBody[stage]}
          </DispatchDetailPane>
        </>
      )}
      <DispatchConfirmDialog
        open={confirmDate !== null && confirmDate === date}
        batchLabel={confirmDate ? formatBatchDay(confirmDate, i18n.language) : ''}
        pendReady={pendReady}
        tasks={tasks}
        anomalies={previewData?.anomalies.length ?? 0}
        refreshing={preview.isFetching}
        onCancel={() => setConfirmDate(null)}
        onConfirm={handleConfirmRun}
      />
    </PageShell>
  );
}
